// TeamBridge design tokens, "Playful" direction (draft, 2026-10-21).
// One place for colour, type, spacing, radii, borders, shadows and motion. Components read these
// (import { color, radius } from '#src/design/tokens'), and GlobalStyles writes them out as CSS
// variables (--tb-*) so plain CSS and inline styles can use the same values.
// Plain ESM with no imports, so node tests can check it (tests/design-tokens.test.mjs).

// Raw palette. Only roles below should be used in components.
export const palette = {
  navy900: '#0B1E36',
  navy: '#102B4C',
  navy700: '#2A4468',
  slate: '#4A5B75',
  cloud: '#E6EAF0',
  white: '#FFFFFF',
  cream: '#FFF9EE',
  cream200: '#FFF1D6',
  yellow: '#FFD568',
  yellow600: '#FFC531',
  yellow100: '#FFF0C4',
  coral: '#FF8FA3',
  coral100: '#FFE3E9',
  coral600: '#B4234A',
  aqua: '#54EFF1',
  aqua100: '#D9FAFB',
  aqua700: '#00686B',
  violet: '#B9A4FF',
  violet100: '#EEE8FF',
  violet700: '#5B3CC4',
  mint: '#79F39B',
  mint100: '#DFFBE8',
  mint700: '#11703A',
  orange: '#FF9F5A',
  orange100: '#FFE9D9',
  red: '#E5484D',
  red100: '#FFE3E3',
  red700: '#B3261E',
  blue: '#3A7BEA',
  blue100: '#E1ECFF',
  blue700: '#1D4FB0'
};
export const color = {
  // Text and outlines. Everything is drawn with ink.
  ink: palette.navy,
  inkSoft: palette.navy700,
  // secondary text
  inkMuted: palette.slate,
  // captions, meta (>= 4.5:1 on every surface below)
  inkInverse: palette.white,
  border: palette.navy,
  divider: palette.cloud,
  // Surfaces
  surface: palette.white,
  // cards, inputs, modals
  surfacePage: palette.cream,
  // page background
  surfaceSunken: palette.cream200,
  // wells, rule boxes
  surfaceInk: palette.navy,
  // dark colour blocks (footer, stats)

  // Action
  primary: palette.yellow,
  // the one main button per screen
  primaryHover: palette.yellow600,
  primarySoft: palette.yellow100,
  onPrimary: palette.navy,
  focus: palette.navy,
  // focus ring, drawn with a yellow halo
  focusHalo: palette.yellow,
  // Status
  success: palette.mint700,
  successSoft: palette.mint100,
  warn: '#7A4D00',
  warnSoft: palette.yellow100,
  warnFill: palette.orange,
  // amber: bars and meters running low (game timers' last third)
  warnFillSoft: palette.orange100,
  // the panel behind an amber timer
  danger: palette.red700,
  dangerSoft: palette.red100,
  info: palette.blue700,
  infoSoft: palette.blue100,
  // Accent for celebrations (confetti, podium) only
  party: palette.orange,
  partySoft: palette.orange100
};

// Game categories: each has a bright base (art backdrops, chips), a soft tint (card and section
// backgrounds) and a deep tone that is safe for text on white or on the tint.
export const category = {
  drawing: {
    label: 'Drawing',
    short: 'Drawing',
    base: palette.coral,
    tint: palette.coral100,
    deep: palette.coral600
  },
  word: {
    label: 'Word games',
    short: 'Words',
    base: palette.aqua,
    tint: palette.aqua100,
    deep: palette.aqua700
  },
  trivia: {
    label: 'Trivia & puzzles',
    short: 'Trivia',
    base: palette.violet,
    tint: palette.violet100,
    deep: palette.violet700
  },
  social: {
    label: 'Get to know you',
    short: 'Social',
    base: palette.mint,
    tint: palette.mint100,
    deep: palette.mint700
  }
};

// Team colours (Codenames, team games).
export const team = {
  red: {
    base: palette.red,
    tint: palette.red100,
    deep: palette.red700
  },
  blue: {
    base: palette.blue,
    tint: palette.blue100,
    deep: palette.blue700
  }
};

// Avatar backdrops cycle through the category tints, so a lobby reads as a mix of colours.
export const avatarBackdrops = [palette.yellow100, palette.aqua100, palette.coral100, palette.violet100, palette.mint100, palette.orange100];
export const font = {
  // "... Fallback": local fonts sized to the web fonts, so the swap doesn't move the page (lib/font-fallback.js).
  family: "Poppins, 'Poppins Fallback', 'Helvetica Neue', Arial, sans-serif",
  script: "Lobster, 'Lobster Fallback', cursive",
  // the wordmark only
  weight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    black: 800
  }
};

// [size px, line-height px, weight]. Phones use the `sm` size from 600px down.
export const type = {
  display: {
    size: 60,
    line: 64,
    weight: 800,
    sm: 40
  },
  h1: {
    size: 44,
    line: 50,
    weight: 800,
    sm: 34
  },
  h2: {
    size: 32,
    line: 40,
    weight: 700,
    sm: 26
  },
  h3: {
    size: 22,
    line: 30,
    weight: 700,
    sm: 20
  },
  h4: {
    size: 18,
    line: 26,
    weight: 600,
    sm: 17
  },
  body: {
    size: 16,
    line: 26,
    weight: 400,
    sm: 16
  },
  small: {
    size: 14,
    line: 22,
    weight: 400,
    sm: 14
  },
  caption: {
    size: 12,
    line: 16,
    weight: 600,
    sm: 12
  }
};

// 4px grid
export const space = {
  0: 0,
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  5: 24,
  6: 32,
  7: 48,
  8: 64,
  9: 96
};
export const radius = {
  sm: 10,
  md: 16,
  lg: 24,
  xl: 32,
  pill: 999
};
export const border = {
  width: 2,
  thick: 3,
  line: `2px solid ${palette.navy}`
};

// Hard offset shadows in ink: the house style. Pressed = no offset (the element moves into it).
export const shadow = {
  sm: `3px 3px 0 0 ${palette.navy}`,
  md: `5px 5px 0 0 ${palette.navy}`,
  lg: `8px 8px 0 0 ${palette.navy}`,
  pressed: `0 0 0 0 ${palette.navy}`,
  focus: `0 0 0 3px ${palette.yellow}, 0 0 0 5px ${palette.navy}`
};
export const motion = {
  fast: '120ms',
  base: '200ms',
  slow: '400ms',
  ease: 'cubic-bezier(.2, .8, .2, 1)',
  spring: 'cubic-bezier(.34, 1.56, .64, 1)',
  // Card hover: lift onto a bigger shadow with a slight tilt. Off with prefers-reduced-motion.
  tilt: 'translate(-3px, -3px) rotate(-1deg)',
  press: 'translate(3px, 3px)'
};
export const layout = {
  maxWidth: 1200,
  gutter: 24,
  gutterSm: 16,
  tapTarget: 44
};
export const z = {
  header: 1000,
  sheet: 1300,
  modal: 1300,
  toast: 9999
};
const kebab = s => s.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
const px = v => typeof v === 'number' && v !== 0 ? `${v}px` : String(v);

// Every token as a CSS custom property: --tb-color-ink, --tb-cat-drawing-tint, --tb-radius-md, ...
export function cssVariables() {
  const vars = [];
  Object.entries(color).forEach(([k, v]) => vars.push([`color-${kebab(k)}`, v]));
  Object.entries(category).forEach(([k, c]) => ['base', 'tint', 'deep'].forEach(t => vars.push([`cat-${k}-${t}`, c[t]])));
  Object.entries(team).forEach(([k, c]) => ['base', 'tint', 'deep'].forEach(t => vars.push([`team-${k}-${t}`, c[t]])));
  vars.push(['font-family', font.family], ['font-script', font.script]);
  Object.entries(type).forEach(([k, t]) => {
    vars.push([`text-${k}-size`, px(t.size)], [`text-${k}-line`, px(t.line)], [`text-${k}-weight`, t.weight]);
  });
  Object.entries(space).forEach(([k, v]) => vars.push([`space-${k}`, px(v)]));
  Object.entries(radius).forEach(([k, v]) => vars.push([`radius-${k}`, px(v)]));
  vars.push(['border-width', px(border.width)], ['border', border.line]);
  Object.entries(shadow).forEach(([k, v]) => vars.push([`shadow-${k}`, v]));
  Object.entries(motion).forEach(([k, v]) => vars.push([`motion-${k}`, v]));
  return `:root{${vars.map(([k, v]) => `--tb-${k}:${v};`).join('')}}`;
}

// WCAG relative-luminance contrast, used by the token test and the /design page.
export function contrast(a, b) {
  const lum = hex => {
    const n = hex.replace('#', '');
    const [r, g, bl] = [0, 2, 4].map(i => parseInt(n.slice(i, i + 2), 16) / 255).map(c => c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
    return 0.2126 * r + 0.7152 * g + 0.0722 * bl;
  };
  const [x, y] = [lum(a), lum(b)].sort((p, q) => q - p);
  return (x + 0.05) / (y + 0.05);
}

// Text/background pairs the components use. All must be >= 4.5:1.
export const textPairs = [['ink', color.ink, 'surface', color.surface], ['ink', color.ink, 'surfacePage', color.surfacePage], ['ink', color.ink, 'surfaceSunken', color.surfaceSunken], ['inkSoft', color.inkSoft, 'surface', color.surface], ['inkMuted', color.inkMuted, 'surface', color.surface], ['inkMuted', color.inkMuted, 'surfacePage', color.surfacePage], ['inkMuted', color.inkMuted, 'surfaceSunken', color.surfaceSunken], ['onPrimary', color.onPrimary, 'primary', color.primary], ['onPrimary', color.onPrimary, 'primaryHover', color.primaryHover], ['inkInverse', color.inkInverse, 'surfaceInk', color.surfaceInk], ['primary', color.primary, 'surfaceInk', color.surfaceInk], ['success', color.success, 'successSoft', color.successSoft], ['warn', color.warn, 'warnSoft', color.warnSoft], ['danger', color.danger, 'dangerSoft', color.dangerSoft], ['danger', color.danger, 'surface', color.surface],
// Game timers: amber and red digits sit on their own soft panel, and on the game panel's category tint.
['warn', color.warn, 'warnFillSoft', color.warnFillSoft], ...Object.entries(category).flatMap(([k, c]) => [['warn', color.warn, `${k}.tint`, c.tint], ['danger', color.danger, `${k}.tint`, c.tint]]), ['info', color.info, 'infoSoft', color.infoSoft], ...Object.entries(category).flatMap(([k, c]) => [['ink', color.ink, `${k}.base`, c.base], ['ink', color.ink, `${k}.tint`, c.tint], [`${k}.deep`, c.deep, `${k}.tint`, c.tint], [`${k}.deep`, c.deep, 'surface', color.surface]]), ...Object.entries(team).flatMap(([k, c]) => [[`team.${k}.deep`, c.deep, `${k}.tint`, c.tint], ['inkInverse', color.inkInverse, `team.${k}.deep`, c.deep]])];
export default {
  palette,
  color,
  category,
  team,
  avatarBackdrops,
  font,
  type,
  space,
  radius,
  border,
  shadow,
  motion,
  layout,
  z
};